/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { CalendarDateRequired } from '#utils/UNSAFE_calendarDateUtils';
import {
  addToCalendarDate,
  addToMonth,
  addToYear,
  compareDates,
  getDaysInMonth
} from '#utils/UNSAFE_calendarDateUtils';
import { useMemo, useReducer, useRef } from 'preact/compat';

// This is the type of the state that is returned from the useDatePickerState reducer.
// This is a single state that will handle the different views of the DatePicker, the
// MonthView, the MonthGridView, and the YearGridView. Initially we have implemented
// the state for the MonthView.
type DatePickerState = {
  /**
   * Specifies the current view the DatePicker is showing.
   */
  currentView: 'monthView' | 'monthsGridView' | 'yearsGridView';

  /**
   * The focusableDate is the one day in the MonthView that has tabIndex: 0 on it.
   * If nothing is selected, the DatePicker initially renders
   * with Today as the focusableDate. The focusableDate doesn't have focus until the user tabs in
   * to the Month View.
   * When the user navigates the days with the arrow keys,
   * the focusableDate changes along with the focusedDate, and they will be the same date.
   * This is a 'roving' index.
   * The focusableDate is never undefined.
   * <p>
   * If the user keyboards to the next month (Fn+Down Arrow on a Mac),
   * then the focusableDate and the focusedDate of the next month
   * is the same day number, or as close as possible to stay in the same month. For example,
   * if the focusedDate and focusableDate is April 2, 2024, the focusedDate and focusableDate for the next month is May 2, 2024.
   * If the focusedDate and focusableDate is February 28, 2024, the focusedDate and focusableDate for the next month is March 28, 2024.
   * If the focusedDate and focusableDate is January 31, 2024, the focusedDate and focusableDate for the next month is February 29, 2024 (2024 is a leap year).
   * </p>
   * <p>
   * If the user clicks the Next Month button in the DatePicker's header, then
   * there is no date that has focus, so the focusedDate is undefined.
   * The focusableDate will be set.
   * For example,
   * if the focusableDate is April 2, 2024, the focusableDate for the next month is May 2, 2024.
   * If the focusableDate is February 28, 2024, the focusableDate for the next month is March 28, 2024.
   * If the focusableDate is January 31, 2024, the focusableDate for the next month is February 29, 2024 (2024 is a leap year).
   *
   * </p>
   */
  focusableDate: CalendarDateRequired;

  /**
   * The focusableMonth is the one month in the MonthsGridView that has tabIndex: 0 on it.
   * If nothing is selected, the DatePicker initially renders with Today's month as the
   * focusableMonth. The focusableMonth doesn't have focus until the user tabs in
   * to the Months Grid View.
   *
   * When the user navigates the month with the arrow keys, the focusableMonth changes
   * along with the focusedMonth, and they will be the same date. This is a 'roving' index.
   *
   * The focusableMonth is never undefined.
   */
  focusableMonth: CalendarDateRequired['month'];

  /**
   * The focusableYear is the one month in the YearsGridView that has tabIndex: 0 on it.
   * If nothing is selected, the DatePicker initially renders with Today's month as the
   * focusableYear. The focusableYear doesn't have focus until the user tabs in
   * to the Months Grid View.
   *
   * When the user navigates the month with the arrow keys, the focusableYear changes
   * along with the focusedYear, and they will be the same date. This is a 'roving' index.
   *
   * The focusableYear is never undefined.
   */
  focusableYear: CalendarDateRequired['year'];

  /**
   * A boolean to indicate if the focus is in the view.
   */
  isViewFocused: boolean;

  /**
   * Specifies whether to hide the previous navigation button is hidden from the header.
   */
  isPreviousNavigationButtonHidden: boolean;

  /**
   * Specifies whether to hide the today navigation button is hidden from the header.
   */
  isTodayNavigationButtonHidden: boolean;

  /**
   * Specifies whether to hide the next navigation button is hidden from the header.
   */
  isNextNavigationButtonHidden: boolean;
};

const datePickerResetActionTypes = ['reset'] as const;

const datePickerViewActionTypes = [
  'monthView',
  'monthsGridView',
  'yearsGridView',
  'viewFocus',
  'viewBlur'
] as const;

const monthViewActionTypes = [
  'daySelected',
  'goToToday',
  'previousDay',
  'nextDay',
  'previousWeek',
  'nextWeek',
  'previousMonth',
  'previousMonthAndFocus',
  'nextMonth',
  'nextMonthAndFocus',
  'previousYear',
  'nextYear',
  'firstDayOfMonth',
  'lastDayOfMonth'
] as const;

const monthsGridViewActionTypes = [
  'previousMonthInGridView',
  'nextMonthInGridView',
  'previousMonthRowInGridView',
  'nextMonthRowInGridView',
  'monthSelected'
] as const;

const yearsGridViewActionTypes = [
  'previousYearInGridView',
  'nextYearInGridView',
  'previousYearRowInGridView',
  'nextYearRowInGridView',
  'previousDecade',
  'previousDecadeAndFocus',
  'nextDecade',
  'nextDecadeAndFocus',
  'yearSelected'
] as const;

type DatePickerViewActionTypes = (typeof datePickerViewActionTypes)[number];
type DatePickerResetActionTypes = (typeof datePickerResetActionTypes)[number];
type MonthViewActionTypes = (typeof monthViewActionTypes)[number];
type MonthsGridViewActionTypes = (typeof monthsGridViewActionTypes)[number];
type YearsGridViewActionTypes = (typeof yearsGridViewActionTypes)[number];

// This is the type of the actions that the hook returns.
type DatePickerStateAction = ReturnType<typeof useDatePickerState>['actions'];

// Type for the action of the datePickerReducer.
type DatePickerViewAction = {
  [Type in DatePickerViewActionTypes]: { type: Type; payload?: undefined };
}[DatePickerViewActionTypes];

type DatePickerResetAction = {
  [Type in DatePickerResetActionTypes]: { type: Type; payload: DatePickerState };
}[DatePickerResetActionTypes];

type MonthViewAction = {
  [Type in MonthViewActionTypes]: { type: Type; payload: CalendarDateRequired };
}[MonthViewActionTypes];

type MonthsGridViewAction = {
  [Type in MonthsGridViewActionTypes]: { type: Type; payload: Pick<CalendarDateRequired, 'month'> };
}[MonthsGridViewActionTypes];

type YearsGridViewAction = {
  [Type in YearsGridViewActionTypes]: { type: Type; payload: Pick<CalendarDateRequired, 'year'> };
}[YearsGridViewActionTypes];

type DatePickerReducerAction =
  | DatePickerResetAction
  | DatePickerViewAction
  | MonthViewAction
  | MonthsGridViewAction
  | YearsGridViewAction;

// A reducer is a plain old function you write that takes the current state,
// and the action to be processed, and returns the state as it should be, based on that action occurring.
// The reducer, not the sender,  determines how a given report (action) affects the state.
const datePickerReducer = (
  state: DatePickerState,
  action: DatePickerReducerAction
): DatePickerState => {
  // On mount, the reducer is initialized with initial state derived from the DatePicker's value.
  // If that value is changed programmatically afterwards, the only way to push that update
  // into the reducer is to define a "reset" action.
  if (action.type === 'reset') {
    return action.payload!;
  }

  switch (action.type) {
    case 'monthView': {
      return {
        ...state,
        currentView: 'monthView',
        isViewFocused: false,
        isNextNavigationButtonHidden: false,
        isPreviousNavigationButtonHidden: false,
        isTodayNavigationButtonHidden: false
      };
    }
    case 'monthsGridView': {
      return {
        ...state,
        currentView: 'monthsGridView',
        focusableMonth: state.focusableDate.month,
        isViewFocused: false,
        isNextNavigationButtonHidden: true,
        isPreviousNavigationButtonHidden: true,
        isTodayNavigationButtonHidden: true
      };
    }
    case 'yearsGridView': {
      return {
        ...state,
        currentView: 'yearsGridView',
        focusableYear: state.focusableDate.year,
        isViewFocused: false,
        isNextNavigationButtonHidden: false,
        isPreviousNavigationButtonHidden: false,
        isTodayNavigationButtonHidden: true
      };
    }
    case 'viewFocus': {
      return state.isViewFocused ? state : { ...state, isViewFocused: true };
    }
    case 'viewBlur': {
      return state.isViewFocused ? { ...state, isViewFocused: false } : state;
    }
    case 'daySelected': {
      return { ...state, isViewFocused: true, focusableDate: action.payload };
    }
    case 'goToToday': {
      // User clicked on the 'Today' icon, which means they want to navigate to show the month of Today
      // payload is Today's date. We also set the focus to Today.
      return { ...state, isViewFocused: true, focusableDate: action.payload };
    }
    case 'previousDay': {
      const previousDay = addToCalendarDate(action.payload, { days: -1 });
      return { ...state, isViewFocused: true, focusableDate: previousDay };
    }
    case 'nextDay': {
      const nextDay = addToCalendarDate(action.payload, { days: 1 });
      return { ...state, isViewFocused: true, focusableDate: nextDay };
    }
    case 'previousWeek': {
      const previousDay = addToCalendarDate(action.payload, { days: -7 });
      return { ...state, isViewFocused: true, focusableDate: previousDay };
    }
    case 'nextWeek': {
      const nextDay = addToCalendarDate(action.payload, { days: 7 });
      return { ...state, isViewFocused: true, focusableDate: nextDay };
    }
    case 'previousMonth':
    case 'previousMonthAndFocus': {
      // PageUp moves focus to the previous month.
      // We also get here if the DatePicker while in month view header's Previous navigation button is pressed.
      const prevMonth = addToCalendarDate(action.payload, { months: -1 });
      return {
        ...state,
        isViewFocused: action.type === 'previousMonthAndFocus',
        focusableDate: prevMonth
      };
    }
    case 'nextMonth':
    case 'nextMonthAndFocus': {
      // Shift PageDown moves focus to the next month. TODO: Confirm this works on a Windows machine.
      // Shift Fn Arrow Down works on my Mac.
      // We also get here if the DatePicker while in month view header's Next navigation button is pressed.
      const nextMonth = addToCalendarDate(action.payload, { months: 1 });
      return {
        ...state,
        isViewFocused: action.type === 'nextMonthAndFocus',
        focusableDate: nextMonth
      };
    }
    case 'previousYear': {
      // Shift Page Up moves focus to the previous year. TODO: Confirm this works on a Windows machine.
      const previousYear = addToCalendarDate(action.payload, { years: -1 });
      return { ...state, isViewFocused: true, focusableDate: previousYear };
    }
    case 'nextYear': {
      const nextYear = addToCalendarDate(action.payload, { years: 1 });
      return { ...state, isViewFocused: true, focusableDate: nextYear };
    }
    case 'firstDayOfMonth': {
      // Home moves focus to the first day of the month (Fn+LeftArrow on a mouse.)
      const currentDate = action.payload;
      const firstDayOfMonthDate = {
        year: currentDate.year,
        month: currentDate.month,
        day: 1
      } as CalendarDateRequired;
      return { ...state, isViewFocused: true, focusableDate: firstDayOfMonthDate };
    }
    case 'lastDayOfMonth': {
      // End moves focus to the last day of the month
      const currentDate = action.payload;
      const lastDay = getDaysInMonth(currentDate.month, currentDate.year);
      const lastDayOfMonthDate = {
        year: currentDate.year,
        month: currentDate.month,
        day: lastDay
      } as CalendarDateRequired;
      return { ...state, isViewFocused: true, focusableDate: lastDayOfMonthDate };
    }

    case 'previousMonthInGridView': {
      const previousMonth = addToMonth(action.payload.month, -1);
      return { ...state, focusableMonth: previousMonth, isViewFocused: true };
    }
    case 'previousMonthRowInGridView': {
      const previousMonth = addToMonth(action.payload.month, -4);
      return { ...state, focusableMonth: previousMonth, isViewFocused: true };
    }
    case 'nextMonthInGridView': {
      const nextMonth = addToMonth(action.payload.month, 1);
      return { ...state, focusableMonth: nextMonth, isViewFocused: true };
    }
    case 'nextMonthRowInGridView': {
      const nextMonth = addToMonth(action.payload.month, 4);
      return { ...state, focusableMonth: nextMonth, isViewFocused: true };
    }
    case 'monthSelected': {
      const monthSteps = action.payload.month - state.focusableDate.month;
      const goToDate = addToCalendarDate(state.focusableDate, { months: monthSteps });
      return {
        ...state,
        currentView: 'monthView',
        focusableDate: goToDate,
        isViewFocused: true,
        isNextNavigationButtonHidden: false,
        isPreviousNavigationButtonHidden: false,
        isTodayNavigationButtonHidden: false
      };
    }

    case 'previousYearInGridView': {
      const previousYear = addToYear(action.payload.year, -1);
      return { ...state, focusableYear: previousYear, isViewFocused: true };
    }
    case 'previousYearRowInGridView': {
      const previousYear = addToYear(action.payload.year, -4);
      return { ...state, focusableYear: previousYear, isViewFocused: true };
    }
    case 'previousDecade':
    case 'previousDecadeAndFocus': {
      const previousYear = addToYear(action.payload.year, -10);
      return {
        ...state,
        focusableYear: previousYear,
        isViewFocused: action.type === 'previousDecadeAndFocus'
      };
    }
    case 'nextYearInGridView': {
      const nextYear = addToYear(action.payload.year, 1);
      return { ...state, focusableYear: nextYear, isViewFocused: true };
    }
    case 'nextYearRowInGridView': {
      const nextYear = addToYear(action.payload.year, 4);
      return { ...state, focusableYear: nextYear, isViewFocused: true };
    }
    case 'nextDecade':
    case 'nextDecadeAndFocus': {
      const nextYear = addToYear(action.payload.year, 10);
      return {
        ...state,
        focusableYear: nextYear,
        isViewFocused: action.type === 'nextDecadeAndFocus'
      };
    }
    case 'yearSelected': {
      const yearSteps = action.payload.year - state.focusableDate.year;
      const goToDate = addToCalendarDate(state.focusableDate, { years: yearSteps });
      return {
        ...state,
        currentView: 'monthView',
        focusableDate: goToDate,
        isViewFocused: true,
        isNextNavigationButtonHidden: false,
        isPreviousNavigationButtonHidden: false,
        isTodayNavigationButtonHidden: false
      };
    }
    default:
      return state;
  }
};

type Props = {
  /**
   * This becomes the state's focusableDate.
   */
  initialFocusableDate: CalendarDateRequired;
};

/**
 * useDatePickerState takes an object with the initialFocusableDate and
 * determines the initial state; i.e., focusableDate and focusedDate. It returns the state
 * and actions a user can take on the DatePicker that will update the state. The actions are functions
 * that call dispatch with the type and payload.
 * @param param0
 * @returns
 */
const useDatePickerState = ({ initialFocusableDate }: Props) => {
  // Create the initial state to pass to our reducer.
  const initialState: DatePickerState = {
    currentView: 'monthView',
    focusableDate: initialFocusableDate,
    focusableMonth: initialFocusableDate.month,
    focusableYear: initialFocusableDate.year,
    isViewFocused: false,
    isNextNavigationButtonHidden: false,
    isPreviousNavigationButtonHidden: false,
    isTodayNavigationButtonHidden: false
  };

  const [state, dispatch] = useReducer(datePickerReducer, initialState);

  // initialFocusableDate is the controlled value passed from the parent.
  const lastValueFromParentRef = useRef<CalendarDateRequired>(initialFocusableDate);

  // If a new value is being pushed from the parent, and that value differs from
  // the current focusableDate of the reducer, then this is a programmatic update
  // and we need to call 'reset' to update the reducer state. Otherwise, the only
  // time the reducer state is updated from the value is on mount.
  if (compareDates(lastValueFromParentRef.current, initialFocusableDate) !== 0) {
    lastValueFromParentRef.current = initialFocusableDate;
    if (compareDates(initialFocusableDate, state.focusableDate) !== 0) {
      dispatch({ type: 'reset', payload: initialState });
    }
  }

  // Using an action creator like this is nice because now each time we want to add an action,
  // we don't have to remember the action constant to use or how the payload should be structured. (from the redux doc)

  // memoize the actions object since we pass this around to our components.
  const actions = useMemo(() => {
    const actionsObject: {
      [Type in
        | DatePickerViewActionTypes
        | MonthViewActionTypes
        | MonthsGridViewActionTypes
        | YearsGridViewActionTypes]: (
        payload: Extract<DatePickerReducerAction, { type: Type }>['payload']
      ) => void;
    } = {} as any;

    datePickerViewActionTypes.forEach((type) => {
      actionsObject[type] = () => dispatch({ type });
    });

    monthViewActionTypes.forEach((type) => {
      actionsObject[type] = (
        payload: Extract<DatePickerReducerAction, { type: MonthViewActionTypes }>['payload']
      ) => dispatch({ type, payload });
    });

    monthsGridViewActionTypes.forEach((type) => {
      actionsObject[type] = (
        payload: Extract<DatePickerReducerAction, { type: MonthsGridViewActionTypes }>['payload']
      ) => dispatch({ type, payload });
    });

    yearsGridViewActionTypes.forEach((type) => {
      actionsObject[type] = (
        payload: Extract<DatePickerReducerAction, { type: YearsGridViewActionTypes }>['payload']
      ) => dispatch({ type, payload });
    });

    return actionsObject;
  }, [dispatch]);

  return { state, actions };
};

export { useDatePickerState };
export type {
  DatePickerState,
  DatePickerStateAction,
  DatePickerViewActionTypes,
  MonthViewActionTypes,
  MonthsGridViewActionTypes,
  YearsGridViewActionTypes
};
